import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authService } from '../services/auth';
import type { User, LoginCredentials, RegisterData, AuthResponse, RolePermissions } from '../types/auth';
import { ROLE_PERMISSIONS } from '../types/auth';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const token = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!(token.value && user.value));
  const userPermissions = computed(() => {
    if (!user.value) return null;
    return ROLE_PERMISSIONS[user.value.userType];
  });

  const hasPermission = computed(() => (permission: keyof RolePermissions) => {
    return userPermissions.value?.[permission] || false;
  });

  // Actions
  const initializeAuth = async () => {
    console.log('Initializing auth...');
    const storedToken = authService.getToken();
    const storedUser = authService.getCurrentUser();
    
    console.log('Stored token:', storedToken ? 'exists' : 'none');
    console.log('Stored user:', storedUser ? 'exists' : 'none');

    if (storedToken && storedUser) {
      console.log('Found stored auth data, setting store values...');
      token.value = storedToken;
      user.value = storedUser;
      
      console.log('Store values set, verifying token...');
      // Verify token is still valid
      const refreshedUser = await authService.refreshUser();
      if (refreshedUser) {
        console.log('Token valid, user refreshed');
        user.value = refreshedUser;
      } else {
        console.log('Token invalid, clearing auth');
        // Token invalid, clear auth
        logout();
      }
    } else {
      console.log('No stored auth data found');
    }
  };

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.user && response.token) {
        console.log('Login successful, saving auth data...');
        user.value = response.user;
        token.value = response.token;
        // Persist token and user to localStorage using authService
        authService.saveAuthData(response.token, response.user);
        
        // Verify it was saved
        const savedToken = authService.getToken();
        const savedUser = authService.getCurrentUser();
        console.log('Verification - saved token:', savedToken ? 'exists' : 'none');
        console.log('Verification - saved user:', savedUser ? 'exists' : 'none');
      } else {
        console.log('Login failed:', response.error);
        error.value = response.error || 'Login failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Login failed';
      return {
        success: false,
        error: err.message || 'Login failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData: RegisterData): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.register(userData);
      
      if (response.success && response.user && response.token) {
        user.value = response.user;
        token.value = response.token;
        // Persist token and user to localStorage using authService
        authService.saveAuthData(response.token, response.user);
      } else {
        error.value = response.error || 'Registration failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Registration failed';
      return {
        success: false,
        error: err.message || 'Registration failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const logout = () => {
    console.log('Logging out, clearing all auth data...');
    authService.clearAuthData();
    // Also clear LiveKit token
    import('../services/api').then(({ apiService }) => {
      apiService.clearLiveKitToken();
    });
    user.value = null;
    token.value = null;
    error.value = null;
    console.log('Logout completed');
  };

  const updateProfile = async (updates: Partial<User>): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.updateProfile(updates);
      
      if (response.success && response.user) {
        user.value = response.user;
      } else {
        error.value = response.error || 'Profile update failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Profile update failed';
      return {
        success: false,
        error: err.message || 'Profile update failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.changePassword(currentPassword, newPassword);
      
      if (!response.success) {
        error.value = response.error || 'Password change failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Password change failed';
      return {
        success: false,
        error: err.message || 'Password change failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const requestPasswordReset = async (email: string): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.requestPasswordReset(email);
      
      if (!response.success) {
        error.value = response.error || 'Password reset request failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Password reset request failed';
      return {
        success: false,
        error: err.message || 'Password reset request failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const resetPassword = async (resetToken: string, newPassword: string): Promise<AuthResponse> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authService.resetPassword(resetToken, newPassword);
      
      if (!response.success) {
        error.value = response.error || 'Password reset failed';
      }

      return response;
    } catch (err: any) {
      error.value = err.message || 'Password reset failed';
      return {
        success: false,
        error: err.message || 'Password reset failed',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    // State
    user,
    token,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    userPermissions,
    hasPermission,
    
    // Actions
    initializeAuth,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    requestPasswordReset,
    resetPassword,
    clearError,
  };
});
